/**
 * Selected-theme preview. Each completed build gets a versioned navigable
 * capability shared by the iframe and explicit Open action; page navigation
 * uses that build's links/assets without starting another build.
 */

import { useEffect, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useBuildSite,
  usePages,
  usePosts,
  useSiteSettings,
  useTags,
  sitesApi,
  isTauri,
  type BuildSkippedPage,
} from '@wordpuppi/shared';
import { openPreparedPreview } from '@/utils/desktopActions';
import { Button } from '@/components/ui/button';
import {
  builtPreviewUrl,
  templatePathToPreviewPath,
  findSkipped,
  THEME_SAMPLE_PREVIEW_PATH,
  type PreviewCtx,
} from './previewRoute';

let nextBuildRevision = 0;

const errorText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

export function PreviewPane({
  siteSlug,
  dirtyPath,
  templatePath,
  previewThemeId,
  previewTheme,
  rebuildNonce = 0,
  note,
}: {
  siteSlug: string;
  /** Path with unsaved edits, if any — the build won't include them. */
  dirtyPath: string | null;
  /** Theme file open in the editor, e.g. `templates/archive.html`. */
  templatePath: string | null;
  /** AB#652: the SELECTED blueprint — the preview builds this one (into `dist-preview/`), not the ⭐. */
  previewThemeId?: number;
  /**
   * AB#644: the selected STOCK theme's bundled id. A stock theme has no
   * blueprint row, so it is previewed by name — same `dist-preview/` build, so
   * the publishable `dist/` is untouched either way. Never set together with
   * `previewThemeId`.
   */
  previewTheme?: string;
  /** Bump to make the pane rebuild — the parent bumps it on every saved file. */
  rebuildNonce?: number;
  note?: string;
}) {
  const queryClient = useQueryClient();
  const buildSite = useBuildSite(siteSlug);
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);
  const buildBusy = useRef(false);
  const mounted = useRef(true);
  const [buildRevision, setBuildRevision] = useState(0);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const { data: pagesData } = usePages(siteSlug, { status: 'published' });
  const { data: postsData } = usePosts(siteSlug, { status: 'published' });
  const { data: tagsData } = useTags(siteSlug);
  // Already fetched by the theme route — same query key, so no extra request.
  const { data: settingsData } = useSiteSettings(siteSlug);

  /** What `dist-preview/` was last built FROM — `null` until the first build settles. */
  const [builtStamp, setBuiltStamp] = useState<string | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  /** Pages the (lenient) preview build left out — the build itself still succeeded. */
  const [skipped, setSkipped] = useState<BuildSkippedPage[]>([]);
  /** Last path the mapping asked for — where a partial/base/static file leaves it. */
  const [sticky, setSticky] = useState('/');

  // Permalinks the SSG emits: pages at /{slug}/, posts at /posts/{slug}/,
  // tags at /tags/{slug}/ (ssg/builder.rs). Only PUBLISHED content is in a
  // build, so re-filter — the list params alone aren't trusted here.
  const pages = (pagesData?.data ?? []).filter((p) => p.status === 'published');
  const posts = (postsData?.data ?? []).filter((p) => p.status === 'published');
  const post = posts[0];
  // AB#845: comments are a paid per-post opt-in, and the discussion partial
  // renders nothing on a post without it — prefer one that has it switched on.
  const settings = settingsData?.data;
  const commentsPost = settings?.comments_enabled
    ? posts.find((p) => settings.comments_post_ids?.includes(p.id))
    : undefined;
  const ctx: PreviewCtx = {
    pagePathByTemplate: Object.fromEntries(
      [...pages]
        .reverse() // first page in list order wins
        .map((p) => [p.template || 'page.html', `/${p.slug}/`]),
    ),
    postPath: post ? `/posts/${post.slug}/` : null,
    commentsPostPath: commentsPost ? `/posts/${commentsPost.slug}/` : null,
    tagPath: tagsData?.data?.[0] ? `/tags/${tagsData.data[0].slug}/` : null,
  };

  const mapped = templatePathToPreviewPath(templatePath, ctx);
  const path = mapped ?? sticky;
  useEffect(() => {
    if (mapped && mapped !== sticky) setSticky(mapped);
  }, [mapped, sticky]);

  const desktop = isTauri();
  const built = builtStamp !== null;

  /** What the pane WANTS built — blueprint (or stock theme) + save counter. */
  const wantStamp = `${previewThemeId ?? ''}:${previewTheme ?? ''}:${rebuildNonce}`;
  /** Either kind of selection builds into `dist-preview/`. */
  const previewing = previewThemeId != null || previewTheme != null;
  /**
   * `dist-preview/` holds what the pane wants. Until it does (blueprint switched,
   * file saved) every byte in there belongs to the PREVIOUS build — don't fetch
   * it and don't render it, or the pane shows the old blueprint under the new
   * one's query key.
   */
  const settled = builtStamp === wantStamp;

  const skip = settled ? findSkipped(skipped, path) : undefined;
  const requestedViewPath = useRef(path);
  const capabilityQuery = (revision: number, buildSkipped = skipped) => ({
    queryKey: ['built-site-preview', siteSlug, wantStamp, revision],
    queryFn: async () => {
      requestedViewPath.current = path;
      const result = await sitesApi.mintBuiltPreview(siteSlug, {
        // A missing mapped page must not poison the shared capability cache.
        // Lenient builds may legitimately omit index.html but render this path.
        path: findSkipped(buildSkipped, '/') ? path : '/',
        ...(previewThemeId != null ? { preview_theme_id: previewThemeId }
          : previewTheme != null ? { preview_theme: previewTheme } : {}),
      });
      if (!result.success || !result.data?.url || !result.data.key) throw new Error(result.error?.message ?? 'Preview link could not be created');
      return result;
    },
    staleTime: Infinity,
    retry: false,
  });
  const page = useQuery({ ...capabilityQuery(buildRevision), enabled: settled && !buildError && !skip && buildRevision > 0 });

  useEffect(() => {
    // Retry only after moving away from a failed mint's path, never on the same failure.
    if (page.isError && requestedViewPath.current !== path && settled && !skip && !buildError) void page.refetch();
  }, [path, page.isError, page.refetch, settled, skip, buildError]);

  const build = async (): Promise<{ revision: number; skipped: BuildSkippedPage[] } | null> => {
    if (buildBusy.current) return null;
    buildBusy.current = true;
    setBuiltStamp(null);
    setBuildError(null);
    if (dirtyPath) {
      toast.info(`Previewing your last saved version — "${dirtyPath}" has unsaved edits.`);
    }
    // Whatever the last build skipped belongs to the last build.
    setSkipped([]);
    try {
      const result = await buildSite.mutateAsync(
        previewThemeId != null
          ? { preview_theme_id: previewThemeId }
          : previewTheme != null
            ? { preview_theme: previewTheme }
            : undefined,
      );
      if (!mounted.current) return null;
      // Failures are shown IN the pane, not as a toast that scrolls away while
      // the user stares at a preview that is silently the previous blueprint.
      // `result.success` is the API envelope (200 even for a failed build) —
      // the build's own verdict is `result.data.success`.
      if (!result.success || !result.data?.success) {
        setBuildError(result.data?.message ?? 'Build failed');
        return null;
      }
      setBuildError(null);
      // A lenient preview build succeeds while leaving pages out (missing
      // template) — those pages get the muted "not renderable" state, not the
      // red one.
      setSkipped(result.data?.skipped ?? []);
      const revision = ++nextBuildRevision;
      setBuildRevision(revision);
      return { revision, skipped: result.data?.skipped ?? [] };
    } catch (err) {
      if (mounted.current) setBuildError(errorText(err));
      return null;
    } finally {
      // Stamped even when the build failed — a blueprint that can't build must
      // not put the auto-build effect in a retry loop.
      buildBusy.current = false;
      if (mounted.current) setBuiltStamp(wantStamp);
    }
  };

  // Auto-build: the effect is skipped while a build is in flight, so flicking
  // through tiles coalesces — when the in-flight one settles this re-runs with
  // the LATEST wantStamp and builds only that.
  useEffect(() => {
    // Nothing selected = a build with neither preview field, which writes the
    // PUBLISHABLE dist/. Never do that on our own — the button still can.
    if (!previewing) return;
    if (buildSite.isPending || buildBusy.current) return;
    if (settled) return;
    // build() is re-created every render — the stamp is the real trigger, so it
    // is deliberately not a dependency.
    void build();
  }, [previewThemeId, previewTheme, buildSite.isPending, wantStamp, settled, builtStamp]);

  const previewUrl = page.data?.data ? builtPreviewUrl(siteSlug, page.data.data, path) : undefined;
  const pending = buildSite.isPending;
  /** A build is owed or in flight; nothing in `dist-preview/` speaks for this pane yet. */
  const building = pending || (!settled && previewing);
  const buildLabel = pending ? 'Building…' : 'Rebuild preview';

  /** New tab/system browser: snapshot the selected build with working navigation. */
  const openInBrowser = async () => {
    if (openingRef.current) return;
    openingRef.current = true;
    setOpening(true);
    try {
      await openPreparedPreview(async () => {
        const built = await build();
        if (built === null) throw new Error('Preview build failed. Check the error in the preview pane.');
        const result = await queryClient.fetchQuery(capabilityQuery(built.revision, built.skipped));
        const url = builtPreviewUrl(siteSlug, result.data!, path);
        if (!url) throw new Error('Invalid preview path');
        return url;
      });
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      openingRef.current = false;
      setOpening(false);
    }
  };

  return (
    <section className="flex flex-col">
      <div className="mb-2 flex min-h-8 flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-medium">
          Preview {built && <span className="font-mono text-xs text-muted-foreground">{path}</span>}
        </h2>
        <div className="flex items-center gap-2">
          {built && (
            <Button variant="ghost" size="sm" onClick={() => void openInBrowser()} disabled={building || opening}>
              {opening ? 'Building preview…' : desktop ? 'Open in browser' : 'Open in new tab'}
            </Button>
          )}
          <Button
            variant="secondary"
            size="sm"
            disabled={pending || opening}
            title="Builds the site from your saved files and shows the page this template renders."
            onClick={async () => {
              try {
                await build();
              } catch (err) {
                toast.error(errorText(err));
              }
            }}
          >
            {buildLabel}
          </Button>
        </div>
      </div>

      <div className="min-h-[64rem] flex-1 overflow-hidden rounded-lg border border-dashed">
        {/* `settled` and buildError both win over an older capability: dist-preview/ can still
            hold the PREVIOUS blueprint's output, so neither a failed build nor a
            build still in flight may show it as this one. */}
        {settled && !buildError && !skip && previewUrl ? (
          <iframe
            key={`${buildRevision}:${path}`}
            src={previewUrl}
            title="Site preview"
            className="h-full min-h-[64rem] w-full bg-white"
            sandbox="allow-scripts"
          />
        ) : (
          <div className="flex h-full min-h-[64rem] flex-col items-center justify-center gap-1 px-6 text-center">
            {buildError ? (
              <>
                <p className="font-medium">Build failed</p>
                <p className="max-w-xs text-sm text-muted-foreground">{buildError}</p>
              </>
            ) : skip ? (
              // The build SUCCEEDED and left this page out — muted, not the red
              // failure state.
              <div className="flex flex-col items-center gap-1 opacity-75">
                <p className="font-medium text-muted-foreground">
                  Not renderable with this blueprint
                </p>
                <p className="max-w-xs text-sm text-muted-foreground">{skip.reason}</p>
                {/* The canned sample isn't a page of the site — that line would lie about it. */}
                {path !== THEME_SAMPLE_PREVIEW_PATH && (
                  <p className="max-w-xs text-sm text-muted-foreground">
                    Your site still builds — the ⭐ default blueprint renders this page.
                  </p>
                )}
              </div>
            ) : !settled ? (
              <>
                <p className="font-medium">{building ? 'Building preview…' : 'Nothing built yet'}</p>
                <p className="max-w-xs text-sm text-muted-foreground">
                  {building
                    ? 'Rendering your saved theme with real content.'
                    : 'Build a preview to see your saved theme rendered with real content.'}
                </p>
              </>
            ) : (
              <>
                <p className="font-medium">{page.isFetching ? 'Loading…' : 'Nothing to show'}</p>
                <p className="max-w-xs text-sm text-muted-foreground">
                  {page.error
                    ? errorText(page.error)
                    : `The last build has no page at ${path} — publish content that uses this template, then rebuild.`}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {note && <p className="mt-2 text-sm text-muted-foreground">{note}</p>}
    </section>
  );
}
